/**
 * The wind register (AD-007k). The *shape* of the model — how many altitude
 * bands there are, where their boundaries sit, the ratio between the bands'
 * wind speeds and what the briefed severity word is worth — is VERIFIED from
 * `RB.EXE`'s compiled-in wind record and lives in `wind.ts` as data, not here.
 * What is left over is the pair of things the original states only in its own
 * arbitrary velocity unit: **how fast** the reference band's wind is in SI, and
 * **which way** it blows.
 *
 * **The westerly is live** (user decision, 2026-08-21; AD-007k §10).
 * `windReferenceMps` is `WIND_MANUAL_REFERENCE_MPS`, and the severity table in
 * `wind.ts` scales it per briefed word, so a mission told *"the winds are
 * still"* flies in still air and one told *"strong"* flies in 9 m/s.
 *
 * That is a port decision and not a reading of the shipped executable, which is
 * stated plainly because AD-007k §4 established the opposite: the original has a
 * complete wind model and a compiled record holding a real profile, and then
 * destroys it — the moment REALISTIC WEATHER is anything but its uninitialised
 * zero the record is overwritten by a `.WND` file, all four of which are
 * byte-identical stubs whose fields sit two bytes off the struct the code reads.
 * Both realism preference files the game ships carry REALISTIC WEATHER *on*, so
 * the overwrite always happens and shipped Red Baron flies in dead air. The port
 * flies the model the executable *carries* rather than the accident that
 * silences it, because the manual documents the mechanic as gameplay and every
 * consumer of it — the crab, the bomb's fall, the briefing word — is now built
 * and censused. Setting `windReferenceMps` back to 0 restores the original's own
 * behaviour exactly, and so does REALISTIC WEATHER off, which is the gate the
 * original itself puts on the whole subsystem.
 */

/**
 * The wind strength the manual's mechanic implies — "the prevailing wind ...
 * usually blows toward the German side", strong enough that a pilot who ignores
 * it on a long sortie comes home short of fuel. ≈13 mph, a moderate breeze.
 *
 * CALIBRATION-PENDING as a *magnitude*: the original's own band-1 `20` is in an
 * internal velocity unit with no recoverable SI conversion (AD-007k §7), so this
 * is the manual read as gameplay rather than a decoded constant, and a drift
 * measurement against the original is what should replace it. It is the
 * reference the severity table hangs off: the briefed word `light` is exactly
 * this, and `strong` is 1.5 × it.
 */
export const WIND_MANUAL_REFERENCE_MPS = 6;

export const WIND_TUNABLES = {
  /**
   * Wind speed in the band the whole practical flight envelope sits in — band 1
   * of `WIND_BAND_CEILINGS_FT`, i.e. everything between 500 ft and the
   * aircraft's ceiling — **before** `WEATHER_WIND_SCALE` applies the mission's
   * briefed word to it. So this is what a `light` mission flies in, and what
   * `still` and `strong` are measured against.
   *
   * Set it to 0 to fly the original's own dead air (the module note above); the
   * whole subsystem is off anyway with REALISTIC WEATHER off, which is where the
   * original puts the switch and where the Realism Panel still puts it.
   */
  windReferenceMps: WIND_MANUAL_REFERENCE_MPS,
  /**
   * Compass direction the wind blows *towards*, radians, 0 = north and
   * increasing clockwise (`math3d.ts`'s `headingAngle` convention).
   *
   * **0, because that is where the German side is in this port's world.** The
   * manual's claim is directional — *"the prevailing wind ... usually blows
   * toward the German side ... the stronger the wind, the more advantage German
   * pilots will have"* — so what has to be right is the axis, not the compass
   * rose: a real westerly blows east, but `mission-world.ts` does not lay the
   * front line out east–west. It lays it out as a plane of constant `z` with
   * friendly territory at `z > 0`, which makes heading 0 (−Z) the direction
   * every briefing means by "over the wire". A wind on heading 0 therefore
   * carries a pilot deeper into enemy territory and holds him back on the way
   * home, which is the mechanic the manual describes.
   *
   * The π/2 this shipped with was the compass answer to the map's question, and
   * in this world it is a pure crosswind on every mission's ingress axis —
   * measured, the least interesting wind the manual describes and the one that
   * does most damage to a bomb run (AD-007k §9.8, §10.1).
   */
  windDirectionRad: 0,
} satisfies Record<string, number>;

export type WindTunableKey = keyof typeof WIND_TUNABLES;

export const WIND_TUNABLE_KEYS: readonly WindTunableKey[] = Object.keys(
  WIND_TUNABLES,
) as WindTunableKey[];
